import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  FileText, 
  DollarSign,
  Calendar,
  Eye,
  MapPin,
  UserCheck,
  Key,
  GitBranch
} from 'lucide-react';
import { ParceiroMetrics } from '@/types/parceiros';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { useIsMobile } from '@/hooks/use-mobile';

interface ParceirosListProps {
  parceiros: ParceiroMetrics[];
  loading: boolean;
  onParceiroClick: (parceiroId: string) => void;
  onParceiroUpdated?: () => void;
}

export function ParceirosList({ parceiros, loading, onParceiroClick, onParceiroUpdated }: ParceirosListProps) {

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-3">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-3 bg-muted rounded w-full"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
                <div className="h-3 bg-muted rounded w-full"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (parceiros.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
          <Users className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mb-4" />
          <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">Nenhum parceiro encontrado</h3>
          <p className="text-sm sm:text-base text-muted-foreground text-center">
            Não há parceiros cadastrados ou que correspondam aos filtros aplicados.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
      {parceiros.map((parceiro) => (
        <ParceiroCard
          key={parceiro.parceiro_id}
          parceiro={parceiro}
          onClick={() => onParceiroClick(parceiro.parceiro_id)}
          onParceiroUpdated={onParceiroUpdated}
        />
      ))}
    </div>
  );
}

interface ParceiroCardProps {
  parceiro: ParceiroMetrics;
  onClick: () => void;
  onParceiroUpdated?: () => void;
}

function ParceiroCard({ parceiro, onClick, onParceiroUpdated }: ParceiroCardProps) {
  const permissions = usePermissions();
  const isAdmin = permissions.isAdmin();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [toggleLoading, setToggleLoading] = useState(false);

  const handleTogglePodeCriarRede = async (novoValor: boolean) => {
    if (!isAdmin || toggleLoading) return;
    const totalRede = parceiro.total_parceiros_rede || 0;
    // Não pode desabilitar se tem parceiros na rede
    if (!novoValor && totalRede > 0) {
      toast({
        title: 'Não é possível desabilitar',
        description: `Remova os ${totalRede} parceiro(s) da rede antes de desabilitar esta opção.`,
        variant: 'destructive'
      });
      return;
    }
    setToggleLoading(true);
    try {
      const res = await fetchWithAuth(`/api/parceiros/${parceiro.parceiro_id}/pode-criar-rede`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pode_criar_rede_parceiros: novoValor })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erro ao atualizar');
      }
      toast({
        title: novoValor ? 'Habilitado' : 'Desabilitado',
        description: novoValor
          ? 'Parceiro agora pode criar rede de parceiros.'
          : 'Parceiro não pode mais criar rede de parceiros.'
      });
      onParceiroUpdated?.();
    } catch (err) {
      toast({
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Erro ao atualizar configuração',
        variant: 'destructive'
      });
    } finally {
      setToggleLoading(false);
    }
  };

  const getPerformanceColor = (taxa: number) => {
    if (taxa >= 20) return 'text-green-600 bg-green-50';
    if (taxa >= 10) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getPerformanceIcon = (taxa: number) => {
    if (taxa >= 10) return <TrendingUp className="h-4 w-4" />;
    return <TrendingDown className="h-4 w-4" />;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evita que o clique no card seja acionado

    toast({
      title: "Aguardando definição de senha",
      description: (
        <div className="space-y-2">
          <p className="text-sm">
            O e-mail de boas-vindas com a senha provisória já foi enviado para <strong>{parceiro.parceiro_email}</strong>.
          </p>
          <p className="text-xs text-muted-foreground">
            Estamos no aguardo de que o parceiro acesse o sistema e defina a senha definitiva para ativar o cadastro.
          </p>
        </div>
      ),
      duration: 8000,
    });
  };

  return (
    <Card className="hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-primary/20 hover:border-l-primary">
      <CardHeader className="pb-3 px-4 sm:px-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
            <Avatar className="h-8 w-8 sm:h-10 sm:w-10 bg-primary/10 flex-shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs sm:text-sm">
                {(() => {
                  const nome = parceiro.parceiro_nome || '';
                  const partes = nome.trim().split(/\s+/).filter(p => p.length > 0);
                  if (partes.length === 0) return 'U';
                  if (partes.length === 1) return partes[0][0].toUpperCase();
                  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
                })()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-xs sm:text-sm truncate">{parceiro.parceiro_nome}</h3>
              <p className="text-xs text-muted-foreground truncate">{parceiro.parceiro_email}</p>
              {parceiro.parceiro_cidade && parceiro.parceiro_estado && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{parceiro.parceiro_cidade}/{parceiro.parceiro_estado}</span>
                </div>
              )}
            </div>
          </div>
          {parceiro.status ? (
            <Badge variant="default" className="flex-shrink-0 text-xs">
              Ativo
            </Badge>
          ) : (
            <Badge 
              variant="secondary" 
              className="cursor-pointer hover:bg-yellow-100 hover:text-yellow-800 transition-colors flex items-center gap-1 flex-shrink-0 text-xs"
              onClick={handleStatusClick}
              title="Aguardando definição de senha pelo parceiro"
            >
              <Key className="h-3 w-3" />
              {!isMobile && 'Inativo'}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
        {/* Data de Início */}
        <div className="flex items-center text-xs text-muted-foreground">
          <Calendar className="h-3 w-3 mr-1" />
          Iniciou em {formatDate(parceiro.data_inicio)}
        </div>

        {/* Vendedor Vinculado - Apenas para Administradores */}
        {isAdmin && (
          <div className="flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-1.5 rounded-md">
            <UserCheck className="h-3 w-3 mr-1.5 flex-shrink-0" />
            <span className="font-medium">Vendedor:</span>
            <span className="ml-1 truncate">{parceiro.vendedor_nome || 'Não vinculado'}</span>
          </div>
        )}

        {/* Métricas Principais (fotovoltaico + carregadores unificados) */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <div className="text-center p-2 sm:p-2.5 bg-blue-50 rounded-md">
            <div className="text-base sm:text-lg font-bold text-blue-600">{parceiro.total_leads}</div>
            <div className="text-xs text-muted-foreground">Leads</div>
            {parceiro.leads_30_dias > 0 && (
              <div className="text-xs text-green-600 mt-1">+{parceiro.leads_30_dias} este mês</div>
            )}
          </div>
          
          <div className="text-center p-2 sm:p-2.5 bg-green-50 rounded-md">
            <div className="text-base sm:text-lg font-bold text-green-600">
              {parceiro.propostas_enviadas + (parceiro.propostas_carregadores_enviadas ?? 0)}
            </div>
            <div className="text-xs text-muted-foreground">Propostas</div>
            {(parceiro.propostas_30_dias || 0) + (parceiro.propostas_carregadores_30_dias ?? 0) > 0 && (
              <div className="text-xs text-green-600 mt-1">
                +{(parceiro.propostas_30_dias || 0) + (parceiro.propostas_carregadores_30_dias ?? 0)} este mês
              </div>
            )}
          </div>
          
          <div className="text-center p-2 sm:p-2.5 bg-purple-50 rounded-md">
            <div className="text-base sm:text-lg font-bold text-purple-600">
              {parceiro.contratos_fechados + (parceiro.contratos_carregadores_fechados ?? 0)}
            </div>
            <div className="text-xs text-muted-foreground">Contratos</div>
            {(parceiro.contratos_30_dias || 0) + (parceiro.contratos_carregadores_30_dias ?? 0) > 0 && (
              <div className="text-xs text-green-600 mt-1">
                +{(parceiro.contratos_30_dias || 0) + (parceiro.contratos_carregadores_30_dias ?? 0)} este mês
              </div>
            )}
          </div>
          
          <div className="text-center p-2 sm:p-2.5 bg-orange-50 rounded-md">
            <div className="text-xs sm:text-sm font-bold text-orange-600 break-words">
              {formatCurrency((parceiro.valor_contratos || 0) + (parceiro.valor_contratos_carregadores ?? 0))}
            </div>
            <div className="text-xs text-muted-foreground">Valor Total</div>
          </div>
        </div>

        {/* Taxa de Conversão (leads → contratos, fotovoltaico + carregadores) */}
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-muted-foreground">Taxa de Conversão</span>
          {(() => {
            const totalContratos = parceiro.contratos_fechados + (parceiro.contratos_carregadores_fechados ?? 0);
            const taxa = parceiro.total_leads > 0 ? Number(((totalContratos / parceiro.total_leads) * 100).toFixed(2)) : 0;
            return (
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getPerformanceColor(taxa)}`}>
                {getPerformanceIcon(taxa)}
                <span className="font-semibold">{taxa}%</span>
              </div>
            );
          })()}
        </div>

        {/* Parceiros Rede - apenas se o parceiro pode criar rede */}
        {(parceiro.pode_criar_rede_parceiros ?? false) && (
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-xs text-muted-foreground">Parceiros Rede</span>
            <span className="text-xs font-semibold">{parceiro.total_parceiros_rede || 0}</span>
          </div>
        )}

        {/* Pode criar rede (apenas Admin) */}
        {isAdmin && (
          <div className="flex items-center justify-between pt-2 border-t" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <GitBranch className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Pode criar rede</span>
            </div>
            <Switch
              checked={parceiro.pode_criar_rede_parceiros ?? false}
              onCheckedChange={handleTogglePodeCriarRede}
              disabled={toggleLoading || ((parceiro.total_parceiros_rede || 0) > 0 && (parceiro.pode_criar_rede_parceiros ?? false))}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Ações */}
        <div className="flex justify-end pt-2">
          <Button size="sm" variant="outline" onClick={onClick} className="w-full">
            <Eye className="h-3 w-3 mr-2" />
            Ver Detalhes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


